import { action, batch } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { pairDtoUpdateReceivedEvent } from '@root/entities/pair'
import { ApiError, apiRequest } from '@root/shared/api'
import { tradeApiClientAtom } from '@root/shared/api/trade-api-client-atom'

import type { PositionId } from '../model/position-id'
import type { TypeHelper } from '@blade/std-lib'
import type { UserPositionsCreatePayload } from '@blade/trade-api-client'
import type { PairAddress } from '@root/entities/pair'
import type { WalletId } from '@root/entities/wallet/model'

export const fetchUserPositionsRequestAction = action(
  async (
    ctx,
    {
      network,
      ...req
    }: TypeHelper.OmitKey<UserPositionsCreatePayload, 'network'> & {
      network: Network.Network
    },
  ) => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userPositionsCreate, [
        {
          ...req,
          network: Network.toDto(network),
        },
      ]),
    )

    if (Result.isSuccess(result)) {
      batch(ctx, () => {
        for (const it of result.success.data) {
          pairDtoUpdateReceivedEvent(ctx, it.pair)
        }
      })
    }

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'fetchUserPositionsRequestAction',
)

export const fetchPositionByIdRequestAction = action(
  async (
    ctx,
    {
      positionId,
      network,
    }: {
      positionId: PositionId
      network: Network.Network
    },
  ) => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userPositionList, [
        {
          positionId,
          network: Network.toDto(network),
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'fetchPositionByIdRequestAction',
)

export const togglePositionVisibilityRequestAction = action(
  async (
    ctx,
    {
      pairAddress,
      network,
    }: { pairAddress: PairAddress; network: Network.Network },
  ) => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userToggleHidePositionsCreate, [
        {
          poolAddress: pairAddress,
          network: Network.toDto(network),
        },
      ]),
    )

    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'togglePositionVisibilityRequestAction',
)

export const fetchActivePositionRequestAction = action(
  async (
    ctx,
    req: {
      pairAddress: PairAddress
      walletId: WalletId
      network: Network.Network
    },
  ) => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userTokenPositionCreate, [
        {
          ...req,
          walletIds: [req.walletId],
          network: Network.toDto(req.network),
        },
      ]),
    )

    if (Result.isSuccess(result) && result.success.position) {
      pairDtoUpdateReceivedEvent(ctx, result.success.position.pair)
    }

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )
    return mappedResult
  },
  'fetchActivePositionRequestAction',
)
